import { useEffect, useRef, useState } from 'react';
import { getHealthPhotoSignedUrl } from '../services/storageService';

/**
 * Hook que dado un record de salud, obtiene y mantiene fresca la signed URL
 * de su foto desde el worker. Mismo patrón que `usePetPhoto` pero con cache
 * key triple (petId + recordId + ext).
 *
 * Devuelve `{ url, isLoading, error }`.
 *  - Si `record.photoExt` es null/undefined: `{ url: null, isLoading: false }`
 *    sin hacer request.
 *  - Si tiene `photoExt`: pide signed URL, cachea, refresca antes del TTL.
 */

const CACHE = new Map(); // key: `${petId}:${recordId}:${ext}` → { url, expiresAt }
const REFRESH_BUFFER_MS = 60 * 1000;

const SUBSCRIBERS = new Map(); // key: recordId → Set<() => void>

const subscribe = (recordId, onInvalidate) => {
  if (!SUBSCRIBERS.has(recordId)) SUBSCRIBERS.set(recordId, new Set());
  SUBSCRIBERS.get(recordId).add(onInvalidate);
  return () => {
    const set = SUBSCRIBERS.get(recordId);
    if (!set) return;
    set.delete(onInvalidate);
    if (set.size === 0) SUBSCRIBERS.delete(recordId);
  };
};

export const invalidateHealthPhotoCache = (recordId) => {
  for (const key of Array.from(CACHE.keys())) {
    if (key.includes(`:${recordId}:`)) CACHE.delete(key);
  }
  const subs = SUBSCRIBERS.get(recordId);
  if (subs) subs.forEach((fn) => fn());
};

const cacheKeyFor = (petId, recordId, ext) => `${petId}:${recordId}:${ext}`;

export const useHealthPhoto = (record) => {
  const petId = record?.petId ?? null;
  const recordId = record?.id ?? null;
  const photoExt = record?.photoExt ?? null;

  const [state, setState] = useState({
    url: null,
    isLoading: false,
    error: null,
  });

  const [tick, setTick] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!recordId) return undefined;
    return subscribe(recordId, () => {
      if (mountedRef.current) setTick((t) => t + 1);
    });
  }, [recordId]);

  useEffect(() => {
    if (!petId || !recordId || !photoExt) {
      setState({ url: null, isLoading: false, error: null });
      return undefined;
    }

    const key = cacheKeyFor(petId, recordId, photoExt);
    const now = Date.now();
    const cached = CACHE.get(key);

    if (cached && cached.expiresAt - now > REFRESH_BUFFER_MS) {
      setState({ url: cached.url, isLoading: false, error: null });
      return undefined;
    }

    let cancelled = false;
    setState((s) => ({ ...s, isLoading: true }));

    getHealthPhotoSignedUrl({ petId, recordId, ext: photoExt })
      .then((result) => {
        if (cancelled || !mountedRef.current) return;
        if (!result) {
          setState({ url: null, isLoading: false, error: null });
          return;
        }
        const ttlMs = (result.expiresInSeconds ?? 600) * 1000;
        CACHE.set(key, {
          url: result.signedUrl,
          expiresAt: Date.now() + ttlMs,
        });
        setState({ url: result.signedUrl, isLoading: false, error: null });
      })
      .catch((err) => {
        if (cancelled || !mountedRef.current) return;
        setState({ url: null, isLoading: false, error: err });
      });

    return () => {
      cancelled = true;
    };
  }, [petId, recordId, photoExt, tick]);

  return state;
};
